import React, { type FC, forwardRef, type RefObject, useEffect, useRef, useState } from 'react'
import {
    Button,
    Flex,
    IconCalendar,
    IconChevronLeft,
    Input,
    type InputProps,
    useDeviceDetection,
    useOnClickOutside,
} from '@dostavkee/web-ui'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { type DateRange, DayPicker } from 'react-day-picker'
import { ru } from 'react-day-picker/locale'

import { Drawer } from '../drawer'
import styles from './calendar-range.module.scss'

const getFormattedValue = (value?: DateRange) => {
    if (value?.from && value?.to) {
        return dayjs(value.from).isSame(value.to, 'day')
            ? dayjs(value.to).format('DD.MM.YYYY')
            : `${dayjs(value.from).format('DD.MM.YYYY')} - ${dayjs(value.to).format('DD.MM.YYYY')}`
    }
}

export interface CalendarRangeProps extends Omit<InputProps, 'onChange' | 'value'> {
    id?: string
    onChange: (value?: DateRange) => void
    value?: DateRange
    getInjectedEl?: () => HTMLElement
    dropdownPosition?: 'left' | 'right'
    hasMaxDate?: boolean
}

const TODAY_DATE = new Date()

export const CalendarRange: FC<CalendarRangeProps> = forwardRef<
    HTMLInputElement,
    CalendarRangeProps
>(
    (
        {
            placeholder,
            onChange,
            value,
            isInvalid,
            errorMessage,
            label,
            id,
            disabled,
            dropdownPosition = 'right',
            hasMaxDate = true,
            ...props
        },
        ref
    ) => {
        const [isOpen, setIsOpen] = useState(false)

        const dropdownRef = useRef<HTMLDivElement>(null)

        const [month, setMonth] = useState<Date>(value?.from || TODAY_DATE)

        const { isMobile, isTablet } = useDeviceDetection()

        const [selected, setSelected] = useState<DateRange | undefined>(value)

        const handleClose = React.useCallback(() => {
            setIsOpen(false)
        }, [])

        useOnClickOutside(dropdownRef as RefObject<HTMLElement>, handleClose)

        useEffect(() => {
            setSelected(value)
            setMonth(value?.from || TODAY_DATE)
        }, [value])

        const handleReset = React.useCallback(() => {
            setSelected(undefined)
            setMonth(TODAY_DATE)
            onChange()
        }, [onChange])

        const handleApply = React.useCallback(() => {
            onChange(selected)
            handleClose()
        }, [onChange, handleClose, selected])

        const renderDayPicker = React.useCallback(
            (numberOfMonths: number) => {
                return (
                    <div className={styles['calendar__day-picker']}>
                        <DayPicker
                            locale={ru}
                            mode='range'
                            numberOfMonths={numberOfMonths}
                            selected={selected}
                            showOutsideDays={false}
                            components={{
                                Chevron: () => <IconChevronLeft size={20} />,
                            }}
                            disabled={
                                hasMaxDate && {
                                    after: TODAY_DATE,
                                }
                            }
                            month={
                                numberOfMonths === 1
                                    ? month
                                    : dayjs(month).subtract(1, 'month').toDate()
                            }
                            onSelect={setSelected}
                            onNextClick={() => {
                                setMonth(dayjs(month).add(1, 'month').toDate())
                            }}
                            onPrevClick={() => {
                                setMonth(dayjs(month).subtract(1, 'month').toDate())
                            }}
                        />
                    </div>
                )
            },
            [month, selected, hasMaxDate]
        )

        const renderInputs = React.useCallback(() => {
            return (
                <Flex grow={1} spacing='m' wrap='nowrap'>
                    <Input
                        label='От'
                        type='button'
                        value={selected?.from && dayjs(selected.from).format('DD.MM.YYYY')}
                        onClick={() => {
                            setMonth(selected?.from || TODAY_DATE)
                        }}
                    />
                    <Input
                        label='До'
                        type='button'
                        value={selected?.to && dayjs(selected.to).format('DD.MM.YYYY')}
                        onClick={() => {
                            setMonth(selected?.to || TODAY_DATE)
                        }}
                    />
                </Flex>
            )
        }, [selected])

        const renderButtons = React.useCallback(() => {
            return (
                <Flex justify='flex-end' spacing='xs'>
                    <Button
                        size='small'
                        variant='secondary'
                        onClick={() => {
                            handleReset()
                        }}
                    >
                        Сбросить
                    </Button>
                    <Button
                        size='small'
                        variant='primary'
                        onClick={() => {
                            handleApply()
                        }}
                    >
                        Применить
                    </Button>
                </Flex>
            )
        }, [handleApply, handleReset])

        if (isMobile || isTablet) {
            return (
                <div className={styles['calendar']}>
                    <Drawer.NestedRoot open={isOpen} onOpenChange={setIsOpen}>
                        <Drawer.Trigger asChild>
                            <Input
                                {...props}
                                ref={ref}
                                disabled={disabled}
                                errorMessage={errorMessage}
                                id={id}
                                isInvalid={isInvalid}
                                label={label}
                                placeholder={placeholder}
                                type='button'
                                value={getFormattedValue(value)}
                                postfixEl={
                                    <IconCalendar
                                        color='var(--text-and-icon-secondary)'
                                        size={22}
                                    />
                                }
                            />
                        </Drawer.Trigger>
                        <Drawer.Content ariaDescription='Выбор даты'>
                            <Drawer.Header title='Выбор даты' />
                            <Drawer.Body>
                                <div className={styles['calendar__wrapper']}>
                                    <div className={styles['calendar__inputs']}>
                                        {renderInputs()}
                                    </div>
                                    <div className={styles['calendar__picker']}>
                                        {renderDayPicker(1)}
                                    </div>
                                    <Flex direction='row' spacing='xs' wrap='nowrap'>
                                        <Button
                                            fullWidth
                                            size='medium'
                                            variant='secondary'
                                            onClick={() => {
                                                handleReset()
                                            }}
                                        >
                                            Сбросить
                                        </Button>
                                        <Drawer.Close asChild>
                                            <Button
                                                fullWidth
                                                size='medium'
                                                variant='primary'
                                                onClick={() => {
                                                    handleApply()
                                                }}
                                            >
                                                Применить
                                            </Button>
                                        </Drawer.Close>
                                    </Flex>
                                </div>
                            </Drawer.Body>
                        </Drawer.Content>
                    </Drawer.NestedRoot>
                </div>
            )
        }

        return (
            <div className={styles['calendar']}>
                <Input
                    {...props}
                    ref={ref}
                    disabled={disabled}
                    errorMessage={errorMessage}
                    id={id}
                    isInvalid={isInvalid}
                    label={label}
                    placeholder={placeholder}
                    postfixEl={<IconCalendar color='var(--text-and-icon-secondary)' size={22} />}
                    type='button'
                    value={getFormattedValue(value)}
                    onClick={() => {
                        setIsOpen(true)
                    }}
                />
                {isOpen && (
                    <div
                        ref={dropdownRef}
                        className={clsx(
                            styles['calendar__dropdown'],
                            styles[`calendar__dropdown--position--${dropdownPosition}`]
                        )}
                    >
                        <Flex direction='column' spacing='xl'>
                            {renderInputs()}
                            {renderDayPicker(2)}
                            {renderButtons()}
                        </Flex>
                    </div>
                )}
            </div>
        )
    }
)

CalendarRange.displayName = 'Calendar'
